import { useCallback, useEffect, useRef, useState } from "react";
import "./GuestContactWidget.css";

/**
 * Public-site contact / lead capture (no auth). Persists via POST /api/public/guest-lead.
 */
export function GuestContactWidget({ apiBase }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const panelRef = useRef(null);

  const close = useCallback(() => {
    setOpen(false);
    setError("");
    if (!done) {
      return;
    }
    setDone(false);
    setName("");
    setEmail("");
    setMessage("");
    setHoneypot("");
  }, [done]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onKey = (e) => {
      if (e.key === "Escape") {
        close();
      }
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, close]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const t = window.setTimeout(() => {
      panelRef.current?.querySelector(".guest-contact-name")?.focus();
    }, 200);
    return () => window.clearTimeout(t);
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await fetch(`${apiBase}/api/public/guest-lead`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          message: message.trim(),
          companyWebsite: honeypot,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.message || `Request failed (${res.status})`);
      }
      setDone(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {!open ? (
        <button
          type="button"
          className="guest-contact-fab"
          onClick={() => setOpen(true)}
          aria-label="Contact AfterMe"
          aria-haspopup="dialog"
        >
          <span className="guest-contact-fab-icon" aria-hidden>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="guest-contact-fab-label">Contact</span>
        </button>
      ) : null}

      {open ? (
        <div className="guest-contact-overlay" role="presentation">
          <button type="button" className="guest-contact-backdrop" aria-label="Close" onClick={close} />
          <div className="guest-contact-dialog" role="dialog" aria-modal="true" aria-label="Contact AfterMe" ref={panelRef}>
            <div className="guest-contact-panel">
              <header className="guest-contact-header">
                <div>
                  <h2 className="guest-contact-title">Send us a message</h2>
                  <p className="guest-contact-sub">We typically reply within three business days.</p>
                </div>
                <button type="button" className="guest-contact-close" onClick={close} aria-label="Close">
                  ×
                </button>
              </header>

              {done ? (
                <div className="guest-contact-body guest-contact-success">
                  <p className="guest-contact-lead">Thank you — we received your message.</p>
                  <p className="guest-contact-muted">
                    Check your inbox for a confirmation email. If you do not see it, look in spam or promotions.
                  </p>
                  <button type="button" className="guest-contact-primary" onClick={close}>
                    Close
                  </button>
                </div>
              ) : (
                <form className="guest-contact-body" onSubmit={submit}>
                  {error ? <p className="guest-contact-error">{error}</p> : null}
                  <label className="guest-contact-label">
                    Name
                    <input
                      className="guest-contact-input guest-contact-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      maxLength={120}
                      autoComplete="name"
                    />
                  </label>
                  <label className="guest-contact-label">
                    Email
                    <input
                      className="guest-contact-input"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                    />
                  </label>
                  <label className="guest-contact-label">
                    How can we help?
                    <textarea
                      className="guest-contact-textarea"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                      minLength={10}
                      maxLength={8000}
                      rows={5}
                      placeholder="Tell us a bit about your question (at least 10 characters)."
                    />
                  </label>
                  <div className="guest-contact-honeypot" aria-hidden="true">
                    <label>
                      Company website
                      <input tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
                    </label>
                  </div>
                  <button type="submit" className="guest-contact-primary" disabled={busy}>
                    {busy ? "Sending…" : "Send message"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
